import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Cast, Eye, EyeOff, Loader2, Save } from "lucide-react";

const PROTOCOLS = [
  { k: "rtmp", label: "RTMP" }, { k: "rtmps", label: "RTMPS" },
  { k: "srt", label: "SRT" }, { k: "hls_push", label: "HLS push" },
  { k: "webrtc", label: "WebRTC" }, { k: "ndi", label: "NDI/IP" },
  { k: "mpegts_udp", label: "MPEG-TS UDP" },
];

const BITRATE_PRESETS = [
  { label: "Low", v: 1200, a: 96 },
  { label: "SD", v: 2500, a: 128 },
  { label: "HD", v: 6000, a: 192 },
  { label: "Max", v: 9000, a: 256 },
];

const LUFS_TARGETS = [
  { val: -23, label: "−23", sub: "EBU R128" },
  { val: -24, label: "−24", sub: "ATSC A/85" },
  { val: -16, label: "−16", sub: "Streaming" },
];

export function DestinationDialog({ open, dest, channelId, onClose, onSaved }) {
  const isNew = !dest;
  const { tenant } = useAuth();
  const houseTarget = tenant?.default_loudnorm_target || -23;
  const [form, setForm] = useState({ name: "", protocol: "rtmps", endpoint: "", stream_key: "", username: "", password: "", playback_url: "", notes: "", enabled: true, encode_preset: "720p25", loudnorm: true, loudnorm_target: -23, loudnorm_tp: -1.0, video_bitrate_kbps: "", audio_bitrate_kbps: "", failover_enabled: false, failover_after_sec: 60, backup_protocol: "rtmp", backup_endpoint: "", backup_stream_key: "", encoder_backend: "ffmpeg", relay_split_enabled: false });
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(dest ? {
      name: dest.name || "", protocol: dest.protocol || "rtmps", endpoint: dest.endpoint || "",
      stream_key: dest.stream_key || "", username: dest.username || "", password: dest.password || "",
      playback_url: dest.playback_url || "", notes: dest.notes || "", enabled: !!dest.enabled,
      encode_preset: dest.encode_preset || "copy",
      loudnorm: dest.loudnorm !== false,
      loudnorm_target: dest.loudnorm_target || -23,
      loudnorm_tp: dest.loudnorm_tp || -1.0,
      video_bitrate_kbps: dest.video_bitrate_kbps || "",
      audio_bitrate_kbps: dest.audio_bitrate_kbps || "",
      failover_enabled: !!dest.failover_enabled,
      failover_after_sec: dest.failover_after_sec || 60,
      backup_protocol: dest.backup_protocol || dest.protocol || "rtmp",
      backup_endpoint: dest.backup_endpoint || "",
      backup_stream_key: dest.backup_stream_key || "",
      encoder_backend: dest.encoder_backend || "ffmpeg",
      relay_split_enabled: !!dest.relay_split_enabled,
    } : { name: "", protocol: "rtmps", endpoint: "", stream_key: "", username: "", password: "", playback_url: "", notes: "", enabled: true, encode_preset: "720p25", loudnorm: true, loudnorm_target: houseTarget, loudnorm_tp: -1.0, video_bitrate_kbps: "", audio_bitrate_kbps: "", failover_enabled: false, failover_after_sec: 60, backup_protocol: "rtmp", backup_endpoint: "", backup_stream_key: "", encoder_backend: "ffmpeg", relay_split_enabled: false });
  }, [open, dest, houseTarget]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.protocol || !form.endpoint) { toast.error("Name / protocol / endpoint are required"); return; }
    if (form.failover_enabled && !form.backup_endpoint.trim()) { toast.error("Backup endpoint is required when auto-failover is on"); return; }
    setBusy(true);
    try {
      const url = isNew
        ? `/channels/${channelId}/cdn-destinations`
        : `/channels/${channelId}/cdn-destinations/${dest.id}`;
      const method = isNew ? "post" : "patch";
      const payload = { ...form };
      if (payload.video_bitrate_kbps === "") payload.video_bitrate_kbps = null;
      if (payload.audio_bitrate_kbps === "") payload.audio_bitrate_kbps = null;
      const r = await api[method](url, payload);
      toast.success(isNew ? "Destination added" : "Destination updated");
      onSaved(r.data, isNew);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const showAuth = ["rtmp", "rtmps", "srt", "hls_push"].includes(form.protocol);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cdn-dest-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Cast className="w-4 h-4 text-fuchsia-400" /> {isNew ? "New destination" : `Edit · ${dest?.name}`}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {isNew ? "Add another simultaneous push destination — each runs its own ffmpeg." : "Update push settings for this destination."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Twitch main / YouTube Live / Facebook" className="mt-1 rounded-sm" data-testid="cdn-form-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Protocol</Label>
            <div className="mt-1 grid grid-cols-4 gap-1">
              {PROTOCOLS.map((p) => (
                <button
                  key={p.k}
                  onClick={() => set("protocol", p.k)}
                  data-testid={`cdn-form-proto-${p.k}`}
                  className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded-sm border ${
                    form.protocol === p.k
                      ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-500"
                      : "border-border text-muted-foreground hover:bg-muted/40"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Endpoint</Label>
            <Input value={form.endpoint} onChange={(e) => set("endpoint", e.target.value)} className="mt-1 rounded-sm font-mono text-xs"
              placeholder={
                form.protocol === "rtmps" ? "rtmps://a.rtmp.youtube.com/live2/" :
                form.protocol === "rtmp"  ? "rtmp://ingest.example.com/live/"  :
                form.protocol === "srt"   ? "srt://relay.example.com:9998"     :
                "protocol://host/path"
              }
              data-testid="cdn-form-endpoint" />
          </div>
          {showAuth && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Stream key / passphrase</Label>
              <div className="mt-1 flex gap-1">
                <Input type={showKey ? "text" : "password"} value={form.stream_key} onChange={(e) => set("stream_key", e.target.value)} className="rounded-sm font-mono text-xs" data-testid="cdn-form-key" />
                <Button variant="outline" size="sm" className="rounded-sm h-9 px-2" onClick={() => setShowKey((v) => !v)}>
                  {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest cursor-pointer">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="cdn-form-enabled" /> Enabled — start pushing immediately after save
          </label>

          {/* Encoder backend (FFmpeg default · GStreamer opt-in) */}
          {["rtmp", "rtmps", "srt"].includes(form.protocol) && (
            <div className="border-t border-border pt-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Encoder backend</Label>
              <div className="mt-1 grid grid-cols-2 gap-1" data-testid="cdn-form-encoder-backend">
                {[["ffmpeg", "FFmpeg · default"], ["gstreamer", "GStreamer · beta"]].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => set("encoder_backend", k)}
                    data-testid={`cdn-form-encoder-${k}`}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded-sm border ${
                      form.encoder_backend === k ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-400" : "border-border text-muted-foreground hover:bg-muted/40"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {form.encoder_backend === "gstreamer" && (
                <div className="text-[10px] font-mono text-cyan-400/80 mt-1 border border-cyan-500/25 bg-cyan-500/5 rounded-sm px-2 py-1.5" data-testid="cdn-form-encoder-gst-note">
                  GStreamer (x264 + AAC) is used for direct single-source pushes only. Continuous concat playout, 14-layer overlays and EBU-R128 loudnorm still run on FFmpeg. Requires GStreamer in the deploy image.
                </div>
              )}
            </div>
          )}

          {/* Relay/playlist split — gap-free RTMP across playlist restarts */}
          {["rtmp", "rtmps", "srt"].includes(form.protocol) && (
            <div className="border-t border-border pt-2">
              <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest cursor-pointer" data-testid="cdn-form-relay-split-label">
                <input type="checkbox" checked={form.relay_split_enabled} onChange={(e) => set("relay_split_enabled", e.target.checked)} data-testid="cdn-form-relay-split" />
                Relay / playlist split — reliability mode (beta)
              </label>
              {form.relay_split_enabled && (
                <div className="text-[10px] font-mono text-emerald-400/80 mt-1 border border-emerald-500/25 bg-emerald-500/5 rounded-sm px-2 py-1.5" data-testid="cdn-form-relay-split-note">
                  A persistent relay holds the RTMP connection while clips are pre-downloaded to local disk and played in batches — playlist restarts never drop push2stream. Applies to the continuous category push only. Run a soak test before trusting it on air.
                </div>
              )}
            </div>
          )}

          {/* Auto-failover to a backup target */}
          <div className="border-t border-border pt-2">
            <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest cursor-pointer" data-testid="cdn-form-failover-label">
              <input type="checkbox" checked={form.failover_enabled} onChange={(e) => set("failover_enabled", e.target.checked)} data-testid="cdn-form-failover" />
              Auto-failover — switch to a backup target if the primary drops
            </label>
            {form.failover_enabled && (
              <div className="mt-2 space-y-2 rounded-sm border border-amber-500/30 bg-amber-500/5 px-2.5 py-2" data-testid="cdn-form-failover-config">
                <div className="text-[10px] font-mono text-amber-400/90">
                  Same ffmpeg/lease redirects here after the primary fails continuously. It re-tests the primary and switches back on recovery. A conflicting standalone destination on the same backup key is auto-disabled while failed over.
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-1">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Protocol</Label>
                    <select
                      value={form.backup_protocol}
                      onChange={(e) => set("backup_protocol", e.target.value)}
                      className="mt-1 w-full rounded-sm bg-background border border-border text-xs font-mono h-9 px-1"
                      data-testid="cdn-form-backup-protocol"
                    >
                      {["rtmp", "rtmps", "srt"].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Backup endpoint</Label>
                    <Input value={form.backup_endpoint} onChange={(e) => set("backup_endpoint", e.target.value)} className="mt-1 rounded-sm font-mono text-xs" placeholder="rtmp://backup.example.com/live/" data-testid="cdn-form-backup-endpoint" />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Backup stream key</Label>
                    <Input value={form.backup_stream_key} onChange={(e) => set("backup_stream_key", e.target.value)} className="mt-1 rounded-sm font-mono text-xs" placeholder="backup stream key" data-testid="cdn-form-backup-key" />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">After (s)</Label>
                    <Input type="number" value={form.failover_after_sec} onChange={(e) => set("failover_after_sec", e.target.value ? parseInt(e.target.value, 10) : 60)} className="mt-1 rounded-sm font-mono text-xs" placeholder="60" data-testid="cdn-form-failover-after" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-border pt-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Broadcast format · H.264 25 fps · AAC stereo 44.1k · 128k</Label>
            <div className="mt-1 grid grid-cols-2 gap-1">
              {[["720p25", "720p · HD"], ["1080p25", "1080p · Full HD"]].map(([p, label]) => (
                <button
                  key={p}
                  onClick={() => set("encode_preset", p)}
                  data-testid={`cdn-form-broadcast-${p}`}
                  className={`text-[11px] font-mono uppercase tracking-widest px-2 py-2 rounded-sm border transition-colors ${
                    form.encode_preset === p ? "border-emerald-500/70 bg-emerald-500/10 text-emerald-400" : "border-border text-muted-foreground hover:bg-muted/40"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-border pt-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Encode preset · advanced</Label>
            <div className="mt-1 grid grid-cols-4 gap-1">
              {["copy", "480p30", "720p25", "720p30", "720p60", "1080p25", "1080p30", "1080p60"].map((p) => (
                <button
                  key={p}
                  onClick={() => set("encode_preset", p)}
                  data-testid={`cdn-form-preset-${p}`}
                  className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-1.5 rounded-sm border ${
                    form.encode_preset === p ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-500" : "border-border text-muted-foreground hover:bg-muted/40"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground mt-1">
              {form.encode_preset === "copy"
                ? (["rtmp", "rtmps"].includes(form.protocol)
                    ? "Pass-through selected — but RTMP needs a real program, so copy is auto-upgraded to 720p25 on push."
                    : "Pass-through · zero CPU · keeps source bitrate & resolution")
                : `Re-encode to ${form.encode_preset} · x264 veryfast · destination-specific ladder`}
            </div>
            {form.encode_preset === "copy" && (
              <div className="text-[10px] font-mono text-amber-500 mt-2 border border-amber-500/30 bg-amber-500/5 rounded-sm px-2 py-1.5" data-testid="cdn-form-bitrate-copy-hint">
                Select a re-encode preset (e.g. 720p25) to set custom video/audio bitrates.
              </div>
            )}
            {form.encode_preset !== "copy" && (
              <>
                <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest cursor-pointer mt-2" data-testid="cdn-form-loudnorm-label">
                  <input type="checkbox" checked={form.loudnorm} onChange={(e) => set("loudnorm", e.target.checked)} data-testid="cdn-form-loudnorm" />
                  EBU R128 loudness — normalise audio to a fixed broadcast level
                </label>
                {form.loudnorm && (
                  <div className="mt-2" data-testid="cdn-form-lufs-targets">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Loudness target (LUFS)</Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {LUFS_TARGETS.map((t) => (
                        <button
                          key={t.val}
                          type="button"
                          onClick={() => set("loudnorm_target", t.val)}
                          data-testid={`cdn-form-lufs-${Math.abs(t.val)}`}
                          title={`${t.label} LUFS · ${t.sub}`}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border leading-tight ${
                            form.loudnorm_target === t.val ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400" : "border-border text-muted-foreground hover:bg-muted/40"
                          }`}
                        >
                          {t.label} LUFS · {t.sub}
                        </button>
                      ))}
                    </div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 block">True-peak ceiling (dBTP)</Label>
                    <div className="mt-1 flex flex-wrap gap-1" data-testid="cdn-form-tp-targets">
                      {[[-1.0, "−1 dBTP", "Broadcast"], [-2.0, "−2 dBTP", "Strict stream"]].map(([v, label, sub]) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => set("loudnorm_tp", v)}
                          data-testid={`cdn-form-tp-${Math.abs(v)}`}
                          title={`${label} · ${sub}`}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border leading-tight ${
                            form.loudnorm_tp === v ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400" : "border-border text-muted-foreground hover:bg-muted/40"
                          }`}
                        >
                          {label} · {sub}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-2">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Quick bitrate presets</Label>
                  <div className="mt-1 flex flex-wrap gap-1" data-testid="cdn-form-bitrate-presets">
                    {BITRATE_PRESETS.map((bp) => {
                      const active = form.video_bitrate_kbps === bp.v && form.audio_bitrate_kbps === bp.a;
                      return (
                        <button
                          key={bp.label}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, video_bitrate_kbps: bp.v, audio_bitrate_kbps: bp.a }))}
                          data-testid={`cdn-form-bitrate-preset-${bp.label.toLowerCase()}`}
                          title={`${bp.v} kbps video · ${bp.a} kbps audio`}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${
                            active ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-500" : "border-border text-muted-foreground hover:bg-muted/40"
                          }`}
                        >
                          {bp.label} · {bp.v % 1000 === 0 ? bp.v / 1000 : (bp.v / 1000).toFixed(1)}M/{bp.a}k
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Video bitrate (kbps)</Label>
                    <Input
                      type="number"
                      value={form.video_bitrate_kbps}
                      onChange={(e) => set("video_bitrate_kbps", e.target.value ? parseInt(e.target.value, 10) : "")}
                      className="mt-1 rounded-sm font-mono text-xs"
                      placeholder="preset default"
                      data-testid="cdn-form-vbr"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Audio bitrate (kbps)</Label>
                    <Input
                      type="number"
                      value={form.audio_bitrate_kbps}
                      onChange={(e) => set("audio_bitrate_kbps", e.target.value ? parseInt(e.target.value, 10) : "")}
                      className="mt-1 rounded-sm font-mono text-xs"
                      placeholder="128"
                      data-testid="cdn-form-abr"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="rounded-sm h-8 text-xs tenant-btn" onClick={submit} disabled={busy} data-testid="cdn-form-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            {isNew ? "Add destination" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
